import { useMessagesStore } from "@/atoms/playground";
import { conversationsApi } from "@/services/conversation-service";
import { ConversationMessageData } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { usePlaygroundRouting } from "@/pages/Playground/routing/playground-routing";
import { conversationQueryKeys } from "@/features/playground/conversation-query-keys";
import { useConversationScope } from "@/hooks/useConversationScope";

const getPendingMessages = <T extends { id: string }>(messages: Array<T>) =>
  messages.filter((message) => message.id.startsWith("temp-"));

type ConversationMessageLike = {
  id: string;
  conversation_id?: string;
  content?: unknown;
  role?: string;
  is_streaming?: boolean;
};

const mergeConversationMessages = <
  T extends ConversationMessageLike,
  U extends ConversationMessageLike,
>(
  persistedMessages: Array<T>,
  pendingMessages: Array<U>,
) => {
  const combinedMessages = [...persistedMessages, ...pendingMessages] as Array<
    T | U
  >;
  const seenIds = new Set<string>();

  return combinedMessages.filter((message) => {
    if (seenIds.has(message.id)) {
      return false;
    }
    seenIds.add(message.id);

    const isPersisted =
      Boolean(message.id) &&
      !message.id.startsWith("temp-") &&
      message.is_streaming === false;

    const hasMatchingTempPeer =
      combinedMessages.length > 1 &&
      combinedMessages.some(
        (candidate) =>
          candidate.id.startsWith("temp-") &&
          candidate.role === message.role &&
          candidate.conversation_id === message.conversation_id &&
          candidate.content === message.content,
      );

    if (isPersisted && hasMatchingTempPeer) {
      return false;
    }

    return true;
  });
};

export const useConversation = (id?: string) => {
  const { setConversationState } = useMessagesStore();
  const { routeNamespace } = usePlaygroundRouting();
  const { scope, identity, requestScope, ready } = useConversationScope();
  const hydratedConversationIdentityRef = useRef<string | undefined>(
    undefined,
  );
  const hydrationIdentity = id
    ? `${identity}:${routeNamespace}:${id}`
    : undefined;

  const { data, isLoading, isError, error } = useQuery<ConversationMessageData>(
    {
      queryKey: conversationQueryKeys.detail(
        scope,
        id ?? "",
        routeNamespace,
      ),
      queryFn: () =>
        conversationsApi.getConversation(
          id ?? "",
          requestScope,
        ) as Promise<ConversationMessageData>,
      enabled: ready && !!id,
      staleTime: Infinity,
    },
  );

  useEffect(() => {
    if (!id) {
      hydratedConversationIdentityRef.current = undefined;
      setConversationState(null, [], {});
      return;
    }

    if (!ready || !data) {
      // conversation not fetched yet: drop the previous conversation's
      // messages, keep this one's optimistic (temp-) messages, and leave
      // the guard open so hydration runs once data lands
      hydratedConversationIdentityRef.current = undefined;
      const messageState = useMessagesStore.getState();
      const pending =
        messageState.conversationScopeIdentity === identity
          ? getPendingMessages(messageState.messages).filter(
              (message) => message.conversation_id === id,
            )
          : [];
      setConversationState(identity, pending, {});
      return;
    }

    if (hydratedConversationIdentityRef.current === hydrationIdentity) {
      return;
    }

    const messageState = useMessagesStore.getState();
    const pendingMessages =
      messageState.conversationScopeIdentity === identity
        ? getPendingMessages(messageState.messages).filter(
            (message) => message.conversation_id === id,
          )
        : [];
    // Optimistic (temp-) messages are written into the conversation query
    // cache during streaming, so data.messages may already contain them.
    // Dedupe by id and suppress a persisted copy when a matching temp peer
    // already exists in the merged set.
    const nextMessages = mergeConversationMessages(
      data.messages,
      pendingMessages,
    );

    setConversationState(identity, nextMessages, data.references ?? {});
    hydratedConversationIdentityRef.current = hydrationIdentity;
  }, [
    data,
    hydrationIdentity,
    id,
    identity,
    ready,
    setConversationState,
  ]);

  useEffect(() => {
    if (isError) {
      toast.error((error as Error)?.message || "Failed to load conversation");
    }
  }, [isError, error]);

  return { data, isLoading, isError, error };
};
